/**
 * AgreementReadinessStrip — Day 24e §8/§9. Thin UI summary of existing rules via GET /agreements/create-readiness
 * (template · participant · funding · branch services · services). No new readiness engine.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { CheckCircle2, AlertTriangle, XCircle, ExternalLink } from 'lucide-react';

const ICON = { OK: <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />, WARN: <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />, BLOCK: <XCircle className="h-3.5 w-3.5 text-red-600" /> };
const CLS = { OK: 'border-emerald-200 bg-emerald-50 text-emerald-800', WARN: 'border-amber-200 bg-amber-50 text-amber-800', BLOCK: 'border-red-200 bg-red-50 text-red-800' };

export function useCreateReadiness({ participantId, branchId, startDate, endDate, fundingType, templateId, enabled }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!enabled || !participantId) return undefined;
    let live = true;
    api.get('/agreements/create-readiness', { params: { participant_id: participantId, branch_id: branchId || undefined, start_date: startDate || undefined, end_date: endDate || undefined, funding_type: fundingType || undefined, template_id: templateId || undefined } })
      .then((r) => { if (live) setData(r.data?.data || null); }).catch(() => { if (live) setData(null); });
    return () => { live = false; };
  }, [participantId, branchId, startDate, endDate, fundingType, templateId, enabled]);
  return data;
}

export function AgreementReadinessStrip({ readiness, extra = [], perms = [] }) {
  if (!readiness) return <p className="text-xs text-muted-foreground" data-testid="agreement-readiness-loading">Checking prerequisites…</p>;
  const checks = [...readiness.checks, ...extra];
  return (
    <div className="space-y-1.5" data-testid="agreement-readiness-strip">
      <div className="flex flex-wrap gap-1.5">
        {checks.map((c) => (
          <span key={c.key} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${CLS[c.state] || CLS.WARN}`} title={c.detail} data-testid={`agreement-readiness-${c.key}`} data-state={c.state}>
            {ICON[c.state]} {c.label}
          </span>
        ))}
      </div>
      {checks.filter((c) => c.state !== 'OK').map((c) => (
        <p key={c.key} className="text-[11px] text-muted-foreground flex flex-wrap items-center gap-1" data-testid={`agreement-readiness-${c.key}-detail`}>
          <span className="font-medium text-foreground">{c.label}:</span> {c.detail}
          {c.action?.href && (!c.action.permission || perms.includes(c.action.permission)) && (
            <Link to={c.action.href} className="underline inline-flex items-center gap-0.5 text-primary" data-testid={`agreement-readiness-${c.key}-action`}>{c.action.label} <ExternalLink className="h-3 w-3" /></Link>
          )}
        </p>
      ))}
    </div>
  );
}

export default AgreementReadinessStrip;
